import flatten from 'lodash/flatten'
import isEmpty from 'lodash/isEmpty'

import React, { useEffect, useRef, useState } from 'react'
import {
  VictoryAxis,
  VictoryChart,
  VictoryLine,
  VictoryPortal,
  VictoryScatter,
  VictoryVoronoiContainer,
} from 'victory'
import NoData from 'pages/insights/no-data'
import Toggle from './toggle'
import Tooltip from './tooltip'
import styles from './index.module.css'

type Props = {
  colors: string[]
  data: any[]
  dates: {
    start: any
    end: any
    range: any
  }
  labels: string[]
  labelsDisabled?: boolean
  loading?: boolean
}

/**
 * Interactive line chart component using Victory.js for data visualization
 * Features toggleable data series, responsive design, and tooltips
 * @param colors - Array of colors for each data series
 * @param data - Array of data series to display
 * @param dates - Date range configuration for x-axis
 * @param labels - Labels for each data series
 * @param labelsDisabled - Whether to disable label toggles
 * @param loading - Whether chart is in loading state
 */
const Chart = (props: Props): React.ReactElement<'div'> => {
  const containerRef = useRef(null)
  const [width, setWidth] = useState()

  const updateWidth = () => {
    if (containerRef.current) {
      setWidth(containerRef.current.getBoundingClientRect().width)
    }
  }

  useEffect(() => {
    updateWidth()
    window.addEventListener('resize', updateWidth)
    return () => {
      window.removeEventListener('resize', updateWidth)
    }
  }, [])
  const [visibility, setVisibility] = useState([])
  useEffect(() => {
    if (props.data) {
      setVisibility([...Array(props.data.length).fill(true)])
    }
  }, [props.data])
  const hasVisibleData = props.data.some(
    (item, index) => visibility[index] && !isEmpty(item),
  )
  const values = props.data
    .map((item, index) => [visibility[index], item])
    .filter(([visible, data]) => visible && !isEmpty(data)) // eslint-disable-next-line no-unused-vars
    .map(([_, data]) => data.map(({ y }) => y))
  const maxValue = Math.max(...flatten(values))
  const tickCount = maxValue > 5 ? 5 : maxValue

  const renderData = (item, index) => {
    if (visibility[index] && !isEmpty(item)) {
      return [
        renderLine(item, props.colors[index]),
        renderScatter(item, props.colors[index]),
      ]
    }
  }

  const renderLine = (data, color) => {
    const style = {
      data: {
        stroke: color,
        strokeWidth: 3,
      },
    }
    return (
      <VictoryLine
        standalone={false}
        data={data}
        interpolation="linear"
        style={style}
      />
    )
  }

  const renderScatter = (data, color) => {
    const style = {
      data: {
        fill: color,
        stroke: '#fff',
        strokeWidth: 1.5,
      },
      labels: {
        fill: '#8a8a8a',
        fontFamily: 'inherit',
        fontSize: 12,
        padding: 14,
        stroke: 'none',
      },
    }
    return (
      <VictoryScatter standalone={false} data={data} size={5} style={style} />
    )
  }

  const renderChart = () =>
    hasVisibleData ? (
      <VictoryChart
        width={width}
        height={320}
        padding={30}
        containerComponent={
          <VictoryVoronoiContainer
            labelComponent={
              <VictoryPortal>
                <Tooltip />
              </VictoryPortal>
            }
            style={{
              // BUG: Tooltips are clipped in Safari if z-index is bigger than 4 (VictoryVoronoiContainer default is 99)
              zIndex: 1,
            }}
            labels={({ datum }) =>
              `${datum.x.format('dddd, MMMM Do')}: ${datum.y}`
            }
          />
        }
      >
        <VictoryAxis
          tickFormat={x => x.toFixed()}
          tickCount={tickCount}
          style={{
            axis: {
              stroke: 'transparent',
            },
            grid: {
              stroke: '#f4f6f8',
              strokeWidth: 2,
            },
            ticks: {
              stroke: 'transparent',
            },
            tickLabels: {
              fill: '#637381',
              fontFamily: 'inherit',
              fontSize: 12,
            },
          }}
          dependentAxis
        />
        <VictoryAxis
          tickValues={props.dates.range}
          tickFormat={x => x.format('MM/DD/YY')}
          style={{
            axis: {
              stroke: 'transparent',
            },
            ticks: {
              stroke: 'transparent',
            },
            tickLabels: {
              fill: '#637381',
              fontFamily: 'inherit',
              fontSize: 12,
            },
          }}
        />
        {props.data.map(renderData)}
      </VictoryChart>
    ) : (
      <NoData height={320} />
    )

  return (
    <div ref={containerRef}>
      {props.loading ? <NoData height={320}>Loading...</NoData> : renderChart()}
      <div className={styles.chartToggles}>
        {props.labels.map((label, index) => (
          <Toggle
            key={index}
            color={props.colors[index]}
            disabled={
              props.loading || props.labelsDisabled || !props.data[index]
            }
            label={label}
            enabled={visibility[index]}
            onToggle={() =>
              setVisibility(
                visibility.map((value, i) => (i === index ? !value : value)),
              )
            }
          />
        ))}
      </div>
    </div>
  )
}

export default Chart
